import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useError } from '../contexts/ErrorContext';
import { extractErrorMessage } from '../utils/errorHandling';

interface UseResendVerificationEmailOptions {
  /** Whether to show success state instead of navigating */
  showSuccessState?: boolean;
  /** Callback after successful resend */
  onSuccess?: () => void;
}

/**
 * Custom hook to handle resending email verification
 * Consolidates duplicate logic across EmailVerificationPending, Login, and VerifyEmail
 */
export const useResendVerificationEmail = (
  options: UseResendVerificationEmailOptions = {}
) => {
  const { addError } = useError();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const resendEmail = async (email: string): Promise<boolean> => {
    if (!email?.trim()) {
      addError('Email address is required');
      return false;
    }

    setIsResending(true);
    setResendSuccess(false);

    try {
      await authApi.requestEmailVerification({ email: email.trim() });

      if (options.showSuccessState) {
        setResendSuccess(true);
      } else {
        navigate('/email-verification-pending', { state: { email: email.trim() } });
      }

      options.onSuccess?.();
      return true;
    } catch (error) {
      const apiError = extractErrorMessage(error, 'Failed to resend verification email');
      addError(apiError.message);
      return false;
    } finally {
      setIsResending(false);
    }
  };

  return {
    resendEmail,
    isResending,
    resendSuccess,
  };
};
